import Button from '@atlaskit/button/new';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRelayEnvironment } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';
import { useRovoWorkspace } from '@townsquare/collab-context';
import { createPlatformSiteAri } from '@townsquare/collab-context/create-site-ari';

import { CustomFieldForm } from '../TeamCustomField/Forms/CustomFieldForm';
import { createCustomFieldMutation } from '../TeamCustomField/mutations/CreateCustomFieldMutation';
import type { TeamCustomFieldType } from '../TeamCustomField/mutations/__generated__/CreateCustomFieldMutation.graphql';
import type { CustomFieldFormValues } from '../TeamCustomField/types';

export const CreateCustomFieldButton = () => {
	const analytics = useAnalytics();
	const { open, close } = usePreviewPanelsActions();
	const environment = useRelayEnvironment();
	const siteId = useRovoWorkspace();
	const scopeId = createPlatformSiteAri(siteId) ?? '';

	const handleSubmit = useCallback(
		(values: CustomFieldFormValues): Promise<void> => {
			const fieldType = values.fieldType?.value as string;
			const cardinality = (values.fieldCardinality ? 'MULTI' : 'SINGLE') as 'MULTI' | 'SINGLE';
			const options =
				fieldType === 'SELECTOR'
					? (values.fieldOptions ?? []).filter(option => option.value.trim() !== '').map(option => option.value)
					: undefined;
			// peopleFormat determines if the resolved type is USER or TEAM.
			// Form validation via isCustomFieldFormValid guarantees these values are
			// defined before onSubmit is called, so the cast is safe here.
			const resolvedType = (fieldType === 'USER' ? values.peopleFormat?.value : fieldType) as TeamCustomFieldType;

			return new Promise((resolve, reject) => {
				createCustomFieldMutation(
					environment,
					{
						customFieldData: {
							name: values.fieldName ?? '',
							description: values.fieldDescription ?? undefined,
							type: resolvedType,
							cardinality,
							options,
						},
						scopeId,
					},
					{
						onCompleted: () => {
							void analytics.track('teamCustomField', 'created', { fieldType: resolvedType, cardinality });
							close();
							resolve();
						},
						onError: () => {
							reject();
						},
					},
				);
			});
		},
		[environment, scopeId, close, analytics],
	);

	const onClick = useCallback(() => {
		void analytics.ui('teamCustomFieldCreateButton', 'clicked', {});
		open({
			panel: {
				id: 'create-custom-field-panel',
				product: 'townsquare',
				content: () => <CustomFieldForm mode="create" onClose={() => close()} onSubmit={handleSubmit} />,
			},
			width: 400,
		});
	}, [analytics, open, close, handleSubmit]);

	return (
		<Button onClick={onClick} appearance="primary">
			<FormattedMessage
				id="townsquare.teams-settings.header.create-custom-field-button"
				defaultMessage="Create field"
				description="Button to create a team custom field"
			/>
		</Button>
	);
};
